import {
  AvailableNotification,
  ChannelOption,
  CronConfig,
  FrequencyType,
  LLMSettings,
  NotificationTriggerResponse,
  SelectedEntityData,
  SettingsState,
  SettingType,
  SlackChannel,
  SlackUser,
  TaskTriggerType,
  WizardStep
} from "../../pages/Settings/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const DEFAULT_CRON_CONFIG: CronConfig = {
  day: 1,      // Monday for weekly, 1st for monthly
  hour: 9,     // 9 AM
  minute: 0,   // :00
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', // User's local timezone
};

const initialState: SettingsState = {
  activeTab: SettingType.SKILLS,
  isLLMSettingsLoading: false,
  llmSettings: null,
  openUserMemoryManageModal: false,
  // Slack notifications (flattened)
  slackTargetsLoading: false,
  slackTargetsError: null,
  slackChannels: [],
  slackUsers: [],
  slackAvailableNotificationsLoading: false,
  slackAvailableNotifications: [],
  slackTriggersLoading: false,
  slackTriggersError: null,
  slackTriggers: [],
  // Wizard (flattened)
  slackWizardIsOpen: false,
  slackWizardMode: 'create',
  slackWizardStep: 'entity_type',
  slackWizardSelectedNotificationKey: null,
  slackWizardSelectedEntityType: null,
  slackWizardSelectedEntities: [],
  slackWizardSelectedEntitiesData: [],
  slackWizardSelectedFrequency: 'daily',
  slackWizardSelectedChannels: [],
  slackWizardSelectedTaskTriggerTypes: [],
  slackWizardSelectionMethod: null,
  slackWizardPendingActiveSelection: null,
  slackWizardSprints: [],
  slackWizardLoadingSprints: false,
  slackWizardHasFetchedSprints: false,
  slackWizardCronConfig: { ...DEFAULT_CRON_CONFIG },
  slackWizardWorkUnitTypeFilter: 'all' as 'all' | 'roadmap' | 'project',
  slackWizardSprintIntegration: null as 'jira' | 'azure' | null,
  slackWizardSprintReportType: 'current',
  // Sync states persisted in Redux
  slackUserSyncRunning: false,
  projectIdNameMap: {},
  roadmapIdNameMap: {},
  strategyMapIdNameMap: {}
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<SettingType>) => {
      state.activeTab = action.payload;
    },
    setLLMSettings: (state, action: PayloadAction<LLMSettings | null>) => {
      state.llmSettings = action.payload;
    },
    updateLLMModel: (state, action: PayloadAction<string>) => {
      state.llmSettings = {
        provider: state?.llmSettings?.provider || "",
        api_key: state?.llmSettings?.api_key || "",
        model: action.payload
      }
    },
    setLLMSettingsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLLMSettingsLoading = action.payload;
    },
    setOpenUserMemoryManageModal: (state, action: PayloadAction<boolean>) => {
      state.openUserMemoryManageModal = action.payload;
    },

    // Slack Notifications Wizard reducers (flattened)
    setSlackWizardOpen: (state, action: PayloadAction<boolean>) => {
      state.slackWizardIsOpen = action.payload;
    },
    setSlackWizardMode: (state, action: PayloadAction<'create' | 'edit'>) => {
      state.slackWizardMode = action.payload;
    },
    setSlackWizardStep: (state, action: PayloadAction<WizardStep>) => {
      state.slackWizardStep = action.payload;
    },
    resetSlackWizard: (state) => {
      state.slackWizardIsOpen = false;
      state.slackWizardMode = 'create';
      state.slackWizardStep = 'entity_type';
      state.slackWizardSelectedNotificationKey = null;
      state.slackWizardSelectedEntityType = null;
      state.slackWizardSelectedEntities = [];
      state.slackWizardSelectedEntitiesData = [];
      state.slackWizardSelectedFrequency = 'daily';
      state.slackWizardSelectedChannels = [];
      state.slackWizardSelectedTaskTriggerTypes = [];
      state.slackWizardSelectionMethod = null;
      state.slackWizardPendingActiveSelection = null;
      state.slackWizardSprints = [];
      state.slackWizardLoadingSprints = false;
      state.slackWizardHasFetchedSprints = false;
      state.slackWizardCronConfig = { ...DEFAULT_CRON_CONFIG };
      state.slackWizardWorkUnitTypeFilter = 'all';
      state.slackWizardSprintIntegration = null;
      state.slackWizardSprintReportType = 'current';
    },
    setSlackWizardSprintIntegration: (state, action: PayloadAction<'jira' | 'azure' | null>) => {
      state.slackWizardSprintIntegration = action.payload;
    },
    setSlackWizardSelectedNotificationKey: (state, action: PayloadAction<string | null>) => {
      state.slackWizardSelectedNotificationKey = action.payload;
    },
    setSlackWizardSelectedEntityType: (state, action: PayloadAction<string | null>) => {
      state.slackWizardSelectedEntityType = action.payload;
    },
    setSlackWizardSelectedEntities: (state, action: PayloadAction<string[]>) => {
      state.slackWizardSelectedEntities = action.payload;
    },
    setSlackWizardSelectedEntitiesData: (state, action: PayloadAction<SelectedEntityData[]>) => {
      state.slackWizardSelectedEntitiesData = action.payload;
    },
    addSlackWizardEntity: (state, action: PayloadAction<string>) => {
      const set = new Set(state.slackWizardSelectedEntities);
      set.add(action.payload);
      state.slackWizardSelectedEntities = Array.from(set);
    },
    addSlackWizardEntityWithData: (state, action: PayloadAction<SelectedEntityData>) => {
      const set = new Set(state.slackWizardSelectedEntities);
      set.add(action.payload.entity_id);
      state.slackWizardSelectedEntities = Array.from(set);
      // Add to entities data if not already present
      const existsInData = state.slackWizardSelectedEntitiesData.some(
        e => e.entity_id === action.payload.entity_id
      );
      if (!existsInData) {
        state.slackWizardSelectedEntitiesData = [...state.slackWizardSelectedEntitiesData, action.payload];
      }
    },
    removeSlackWizardEntity: (state, action: PayloadAction<string>) => {
      state.slackWizardSelectedEntities = state.slackWizardSelectedEntities.filter(id => id !== action.payload);
      state.slackWizardSelectedEntitiesData = state.slackWizardSelectedEntitiesData.filter(
        e => e.entity_id !== action.payload
      );
    },
    clearSlackWizardEntities: (state) => {
      state.slackWizardSelectedEntities = [];
      state.slackWizardSelectedEntitiesData = [];
    },
    setSlackWizardSelectionMethod: (state, action: PayloadAction<'active' | 'selective' | null>) => {
      state.slackWizardSelectionMethod = action.payload;
    },
    setSlackWizardPendingActiveSelection: (state, action: PayloadAction<{ type: 'strategy_map' | 'work_unit'; limit: number } | null>) => {
      state.slackWizardPendingActiveSelection = action.payload;
    },
    setSlackWizardSelectedFrequency: (state, action: PayloadAction<FrequencyType>) => {
      state.slackWizardSelectedFrequency = action.payload;
    },
    setSlackWizardSelectedChannels: (state, action: PayloadAction<ChannelOption[]>) => {
      state.slackWizardSelectedChannels = action.payload;
    },
    setSlackWizardSelectedTaskTriggerTypes: (state, action: PayloadAction<TaskTriggerType[]>) => {
      state.slackWizardSelectedTaskTriggerTypes = action.payload;
    },
    setSlackWizardSprints: (state, action: PayloadAction<Record<string, unknown>[]>) => {
      state.slackWizardSprints = action.payload;
    },
    setSlackWizardLoadingSprints: (state, action: PayloadAction<boolean>) => {
      state.slackWizardLoadingSprints = action.payload;
    },
    setSlackWizardHasFetchedSprints: (state, action: PayloadAction<boolean>) => {
      state.slackWizardHasFetchedSprints = action.payload;
    },
    setSlackWizardCronConfig: (state, action: PayloadAction<CronConfig>) => {
      state.slackWizardCronConfig = action.payload;
    },
    setSlackWizardWorkUnitTypeFilter: (state, action: PayloadAction<'all' | 'roadmap' | 'project'>) => {
      state.slackWizardWorkUnitTypeFilter = action.payload;
    },
    setSlackWizardSprintReportType: (state, action: PayloadAction<'current' | 'past'>) => {
      state.slackWizardSprintReportType = action.payload;
    },

    // Slack Notifications (data) reducers (flattened)
    setSlackTargetsLoading: (state, action: PayloadAction<boolean>) => {
      state.slackTargetsLoading = action.payload;
    },
    setSlackChannels: (state, action: PayloadAction<SlackChannel[]>) => {
      state.slackChannels = action.payload;
    },
    setSlackUsers: (state, action: PayloadAction<SlackUser[]>) => {
      state.slackUsers = action.payload;
    },
    setSlackAvailableNotificationsLoading: (state, action: PayloadAction<boolean>) => {
      state.slackAvailableNotificationsLoading = action.payload;
    },
    setSlackAvailableNotifications: (state, action: PayloadAction<AvailableNotification[]>) => {
      state.slackAvailableNotifications = action.payload;
    },
    setSlackTriggersLoading: (state, action: PayloadAction<boolean>) => {
      state.slackTriggersLoading = action.payload;
    },
    setSlackTriggers: (state, action: PayloadAction<NotificationTriggerResponse[]>) => {
      state.slackTriggers = action.payload;
    },
    setSlackTargetsError: (state, action: PayloadAction<string | null>) => {
      state.slackTargetsError = action.payload;
    },
    setSlackTriggersError: (state, action: PayloadAction<string | null>) => {
      state.slackTriggersError = action.payload;
    },

    // Slack users sync (persisted)
    setSlackUserSyncRunning: (state, action: PayloadAction<boolean>) => {
      state.slackUserSyncRunning = action.payload;
    },
    // Merge name maps for ID -> name resolution
    mergeProjectNamesById: (state, action: PayloadAction<Record<string, string>>) => {
      state.projectIdNameMap = {
        ...(state.projectIdNameMap || {}),
        ...(action.payload || {})
      };
    },
    mergeRoadmapNamesById: (state, action: PayloadAction<Record<string, string>>) => {
      state.roadmapIdNameMap = {
        ...(state.roadmapIdNameMap || {}),
        ...(action.payload || {})
      };
    },
    mergeStrategyMapNamesById: (state, action: PayloadAction<Record<string, string>>) => {
      state.strategyMapIdNameMap = {
        ...(state.strategyMapIdNameMap || {}),
        ...(action.payload || {})
      };
    }
  }
});

export const {
  setActiveTab,
  setLLMSettings,
  updateLLMModel,
  setLLMSettingsLoading,
  setOpenUserMemoryManageModal,
  setSlackWizardOpen,
  setSlackWizardMode,
  setSlackWizardStep,
  resetSlackWizard,
  setSlackWizardSelectedNotificationKey,
  setSlackWizardSelectedEntityType,
  setSlackWizardSelectedEntities,
  setSlackWizardSelectedEntitiesData,
  addSlackWizardEntity,
  addSlackWizardEntityWithData,
  removeSlackWizardEntity,
  clearSlackWizardEntities,
  setSlackWizardSelectionMethod,
  setSlackWizardPendingActiveSelection,
  setSlackWizardSelectedFrequency,
  setSlackWizardSelectedChannels,
  setSlackWizardSelectedTaskTriggerTypes,
  setSlackWizardSprints,
  setSlackWizardLoadingSprints,
  setSlackWizardHasFetchedSprints,
  setSlackWizardCronConfig,
  setSlackWizardWorkUnitTypeFilter,
  setSlackWizardSprintIntegration,
  setSlackWizardSprintReportType,
  setSlackTargetsLoading,
  setSlackChannels,
  setSlackUsers,
  setSlackAvailableNotificationsLoading,
  setSlackAvailableNotifications,
  setSlackTriggersLoading,
  setSlackTriggers,
  setSlackTargetsError,
  setSlackTriggersError,
  setSlackUserSyncRunning,
  mergeProjectNamesById,
  mergeRoadmapNamesById,
  mergeStrategyMapNamesById
} = settingsSlice.actions;

export default settingsSlice.reducer;